import React, { useEffect, useState } from "react";
import {
  X, Plus, Save, Loader2, Image, Palette, Globe, MessageSquare,
  Layout, Settings, ChevronDown, ChevronUp, Trash2, Upload
} from "lucide-react";
import { postRequest } from "../../../api/postRequest";
import { postFormRequest } from "../../../api/postFormRequest";

/* ─────────────────────────────────────────────
   Types
───────────────────────────────────────────── */
interface ApiResponse<T> {
  success: boolean;
  errorCode?: string;
  errorDetail?: string;
  data?: T;
}

export interface HomeConfig {
  theme: {
    gradientHero: string;
    backgroundColor?: string;
    accent: string;
    accentHover: string;
    textPrimary: string;
    textSecondary: string;
  };
  brand?: { logoUrl?: string; tagline?: string; siteName?: string; siteTagline?: string };
  hero: { title: string; subtitle: string; primaryCta: string; secondaryCta: string };
  heroSlider?: Array<{ imageUrl: string; headline?: string; caption?: string; ctaLabel?: string; ctaHref?: string }>;
  announcements: Array<{ text: string; to?: string }>;
  footer: {
    companyName: string;
    companyTagline: string;
    description: string;
    whatsappNumber: string;
    whatsappButtonText: string;
    phoneDisplay: string;
    phoneHref: string;
    mailDisplay: string;
    mailHref: string;
    addressLine: string;
    contactPerson: string;
    website: string;
    socialLinks: Array<{ platform: string; label: string; href: string }>;
    trustBadges: Array<{ icon: string; title: string; desc: string }>;
    bottomLinks: Array<{ label: string; to: string }>;
  };
  sections: {
    featured: { title: string; subtitle: string; buttonText: string };
    stats: { title: string; subtitle: string };
    process: { title: string; subtitle: string };
    services: { title: string; subtitle: string };
    faqs: { title: string; subtitle: string };
    contact: { title: string; subtitle: string };
  };
  services: Array<{ icon: string; title: string; description: string }>;
  processSteps: Array<{ icon: string; title: string; desc: string }>;
  stats: Array<{ icon: string; number: string; label: string }>;
  pricing: Array<{ tier: string; price: string; features: string[] }>;
  faqs: Array<{ q: string; a: string }>;
  contact: { phone: string; email: string; address: string };
  mapEmbedUrl: string;
  settings?: { odemesizSiparisAktifMi?: boolean };
  notifications?: { smsPhone?: string; smsAktif?: boolean };
}

/* ─────────────────────────────────────────────
   Default config
───────────────────────────────────────────── */
const defaultConfig: HomeConfig = {
  theme: {
    gradientHero: "linear-gradient(135deg, rgba(14,14,16,0.88), rgba(247,201,72,0.88))",
    backgroundColor: "#0E0E10",
    accent: "#F7C948",
    accentHover: "#C99A2E",
    textPrimary: "#FFFFFF",
    textSecondary: "#EDE7D6",
  },
  brand: { logoUrl: "", tagline: "", siteName: "PORTAKAL3D", siteTagline: "3D Yazıcı & Filament" },
  hero: { title: "", subtitle: "", primaryCta: "Hizmetlerimiz", secondaryCta: "İletişime Geç" },
  heroSlider: [],
  announcements: [{ text: "1500 TL ve üzeri kargo ücretsiz", to: "/products" }],
  footer: {
    companyName: "PORTAKAL3D",
    companyTagline: "3D Yazıcı & Filament",
    description: "",
    whatsappNumber: "905432654524",
    whatsappButtonText: "WhatsApp'tan Yaz",
    phoneDisplay: "+90 543 265 45 24",
    phoneHref: "tel:+905432654524",
    mailDisplay: "portakal3d@hotmail.com",
    mailHref: "mailto:portakal3d@hotmail.com",
    addressLine: "",
    contactPerson: "",
    website: "www.portakal3d.com",
    socialLinks: [{ platform: "Instagram", label: "Instagram", href: "" }],
    trustBadges: [
      { icon: "Truck", title: "Hızlı Kargo", desc: "Stoklu ürünlerde aynı gün" },
      { icon: "ShieldCheck", title: "Güvenli Alışveriş", desc: "SSL & güvenli ödeme" },
      { icon: "CreditCard", title: "Ödeme Seçenekleri", desc: "K.Kartı / Havale" },
      { icon: "Phone", title: "Teknik Destek", desc: "Hızlı dönüş" },
    ],
    bottomLinks: [
      { label: "Mesafeli Satış Sözleşmesi", to: "/mesafelisatissozlesmesi" },
      { label: "Teslimat & İade", to: "/teslimatdegisimiadekosullari" },
      { label: "Gizlilik Politikası", to: "/gizlilikpolitikasi" },
      { label: "Kullanıcı Sözleşmesi", to: "/kullanicisozlesmesi" },
    ],
  },
  sections: {
    featured: { title: "Öne Çıkan Ürünler", subtitle: "Popüler ürünleri hemen keşfet.", buttonText: "Tüm ürünler" },
    stats: { title: "Hızlı Bakış", subtitle: "" },
    process: { title: "Çalışma Sürecimiz", subtitle: "" },
    services: { title: "Hizmetlerimiz", subtitle: "" },
    faqs: { title: "Sıkça Sorulan Sorular", subtitle: "" },
    contact: { title: "Bize Ulaşın", subtitle: "" },
  },
  services: [],
  processSteps: [],
  stats: [],
  pricing: [],
  faqs: [],
  contact: { phone: "", email: "", address: "" },
  mapEmbedUrl: "",
  settings: { odemesizSiparisAktifMi: false },
  notifications: { smsPhone: "", smsAktif: true },
};

/* ─────────────────────────────────────────────
   Small UI helpers
───────────────────────────────────────────── */
const Field: React.FC<{
  label: string;
  hint?: string;
  children: React.ReactNode;
  col2?: boolean;
}> = ({ label, hint, children, col2 }) => (
  <div className={col2 ? "md:col-span-2" : ""}>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {hint && <p className="text-xs text-gray-400 mb-1">{hint}</p>}
    {children}
  </div>
);

const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input
    {...props}
    className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${props.className ?? ""}`}
  />
);

const Textarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = (props) => (
  <textarea
    {...props}
    className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${props.className ?? ""}`}
  />
);

const Card: React.FC<{ title: string; icon?: React.ReactNode; children: React.ReactNode; action?: React.ReactNode }> = ({ title, icon, children, action }) => (
  <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
      <div className="flex items-center gap-2">
        {icon && <span className="text-gray-400">{icon}</span>}
        <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Toggle: React.FC<{ checked: boolean; onChange: () => void }> = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${checked ? "bg-blue-600" : "bg-gray-300"}`}
  >
    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${checked ? "translate-x-5" : "translate-x-0"}`} />
  </button>
);

const DeleteBtn: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button type="button" onClick={onClick} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0">
    <Trash2 size={15} />
  </button>
);

const AddBtn: React.FC<{ onClick: () => void; label: string }> = ({ onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium mt-2"
  >
    <Plus size={15} /> {label}
  </button>
);

/* ─────────────────────────────────────────────
   Tab definitions
───────────────────────────────────────────── */
type TabId = "marka" | "icerik" | "iletisim" | "sayfalar" | "ayarlar";
const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: "marka",    label: "Marka & Tema",     icon: <Palette size={15} /> },
  { id: "icerik",   label: "İçerik",           icon: <Layout size={15} /> },
  { id: "iletisim", label: "İletişim & Footer", icon: <Globe size={15} /> },
  { id: "sayfalar", label: "Sayfalar",         icon: <MessageSquare size={15} /> },
  { id: "ayarlar",  label: "Ayarlar",          icon: <Settings size={15} /> },
];

/* ─────────────────────────────────────────────
   Main Component
───────────────────────────────────────────── */
export const AdminHomeConfigPage: React.FC = () => {
  const [config, setConfig] = useState<HomeConfig>(defaultConfig);
  const [recordId, setRecordId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>("marka");

  // Slider foto yönetimi
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [removedUrls, setRemovedUrls] = useState<string[]>([]);

  // Logo yönetimi
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  useEffect(() => { loadConfig(); }, []);

  /* ── API ── */
  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await postRequest<{}, ApiResponse<Array<{ id: number; bilgiler: string | null }>>>(
        "/AnaEkranVerileri/listele", {}
      );
      const list = res.data ?? [];
      if (res.success && list.length > 0) {
        const rec = list[0];
        if (rec.bilgiler?.trim()) {
          try {
            const raw = JSON.parse(rec.bilgiler) as Partial<HomeConfig>;
            const rf = (raw.footer ?? {}) as Partial<HomeConfig["footer"]>;
            const rs = (raw.sections ?? {}) as Partial<HomeConfig["sections"]>;
            setConfig({
              ...defaultConfig, ...raw,
              theme:        { ...defaultConfig.theme,   ...(raw.theme   ?? {}) },
              brand:        { ...defaultConfig.brand,   ...(raw.brand   ?? {}) },
              hero:         { ...defaultConfig.hero,    ...(raw.hero    ?? {}) },
              contact:      { ...defaultConfig.contact, ...(raw.contact ?? {}) },
              heroSlider:   Array.isArray(raw.heroSlider)   ? raw.heroSlider   : defaultConfig.heroSlider,
              announcements:Array.isArray(raw.announcements)? raw.announcements: defaultConfig.announcements,
              services:     Array.isArray(raw.services)     ? raw.services     : defaultConfig.services,
              processSteps: Array.isArray(raw.processSteps) ? raw.processSteps : defaultConfig.processSteps,
              stats:        Array.isArray(raw.stats)        ? raw.stats        : defaultConfig.stats,
              pricing:      Array.isArray(raw.pricing)      ? raw.pricing      : defaultConfig.pricing,
              faqs:         Array.isArray(raw.faqs)         ? raw.faqs         : defaultConfig.faqs,
              mapEmbedUrl:  typeof raw.mapEmbedUrl === "string" && raw.mapEmbedUrl.trim() ? raw.mapEmbedUrl : defaultConfig.mapEmbedUrl,
              settings:      { ...defaultConfig.settings,      ...(raw.settings      ?? {}) },
              notifications: { ...defaultConfig.notifications, ...(raw.notifications ?? {}) },
              footer: {
                ...defaultConfig.footer, ...rf,
                socialLinks:  Array.isArray(rf.socialLinks)  ? rf.socialLinks  : defaultConfig.footer.socialLinks,
                trustBadges:  Array.isArray(rf.trustBadges)  ? rf.trustBadges  : defaultConfig.footer.trustBadges,
                bottomLinks:  Array.isArray(rf.bottomLinks)  ? rf.bottomLinks  : defaultConfig.footer.bottomLinks,
              },
              sections: {
                featured: { ...defaultConfig.sections.featured, ...(rs.featured ?? {}) },
                stats:    { ...defaultConfig.sections.stats,    ...(rs.stats    ?? {}) },
                process:  { ...defaultConfig.sections.process,  ...(rs.process  ?? {}) },
                services: { ...defaultConfig.sections.services, ...(rs.services ?? {}) },
                faqs:     { ...defaultConfig.sections.faqs,     ...(rs.faqs     ?? {}) },
                contact:  { ...defaultConfig.sections.contact,  ...(rs.contact  ?? {}) },
              },
            });
          } catch { /* JSON parse hatası — default kalsın */ }
        }
        setRecordId(rec.id);
      }
    } catch { alert("Veri yüklenirken hata oluştu."); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setLoading(true);
    setSaved(false);
    try {
      // 1) Logo yükle
      let finalLogoUrl = config.brand?.logoUrl ?? "";
      if (logoFile) {
        const fd = new FormData();
        fd.append("Dosyalar", logoFile);
        const r = await postFormRequest<ApiResponse<{ yuklenenFotoUrlListesi: string[] }>>("/Fotograf/yukle", fd);
        if (!r.success || !r.data?.yuklenenFotoUrlListesi?.length) throw new Error(r.errorDetail || "Logo yükleme başarısız.");
        finalLogoUrl = r.data.yuklenenFotoUrlListesi[0];
      }

      // 2) Slider fotoları yükle
      let uploaded: string[] = [];
      if (newFiles.length) {
        const fd = new FormData();
        newFiles.forEach(f => fd.append("Dosyalar", f));
        const r = await postFormRequest<ApiResponse<{ yuklenenFotoUrlListesi: string[] }>>("/Fotograf/yukle", fd);
        if (!r.success || !r.data?.yuklenenFotoUrlListesi?.length) throw new Error(r.errorDetail || "Fotoğraf yükleme başarısız.");
        uploaded = r.data.yuklenenFotoUrlListesi;
      }

      // 3) Config kaydet
      const toSave: HomeConfig = {
        ...config,
        brand: { ...config.brand, logoUrl: finalLogoUrl },
        heroSlider: [
          ...(config.heroSlider ?? []),
          ...uploaded.map(u => ({ imageUrl: u, headline: "", caption: "" })),
        ],
      };
      const payload = recordId
        ? { Id: recordId, Bilgiler: JSON.stringify(toSave) }
        : { Bilgiler: JSON.stringify(toSave) };
      const url = recordId ? "/AnaEkranVerileri/guncelle" : "/AnaEkranVerileri/ekle";
      const r = await postRequest<typeof payload, ApiResponse<string>>(url, payload);
      if (!r.success) throw new Error(r.errorDetail || "Kaydetme başarısız.");

      // 4) Silinen fotoları temizle
      if (removedUrls.length) {
        try { await postRequest("/Fotograf/sil", { UrlList: removedUrls }); } catch {}
      }

      setNewFiles([]);
      setRemovedUrls([]);
      setLogoFile(null);
      setLogoPreview(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await loadConfig();
    } catch (e: any) {
      alert("Kaydetme hatası: " + (e?.message ?? "Bilinmeyen hata"));
    } finally { setLoading(false); }
  };

  /* ── Setter helpers ── */
  const setTheme  = (k: keyof HomeConfig["theme"], v: string)   => setConfig(c => ({ ...c, theme:   { ...c.theme,   [k]: v } }));
  const setBrand  = (k: keyof NonNullable<HomeConfig["brand"]>, v: string) => setConfig(c => ({ ...c, brand: { ...(c.brand ?? {}), [k]: v } }));
  const setHero   = (k: keyof HomeConfig["hero"], v: string)    => setConfig(c => ({ ...c, hero:    { ...c.hero,    [k]: v } }));
  const setFooter = (k: keyof HomeConfig["footer"], v: any)     => setConfig(c => ({ ...c, footer:  { ...c.footer,  [k]: v } }));
  const setContact= (k: keyof HomeConfig["contact"], v: string) => setConfig(c => ({ ...c, contact: { ...c.contact, [k]: v } }));
  const setSection= (sec: keyof HomeConfig["sections"], k: string, v: string) =>
    setConfig(c => ({ ...c, sections: { ...c.sections, [sec]: { ...(c.sections[sec] as any), [k]: v } } }));

  const listAdd    = (key: keyof HomeConfig, item: object) =>
    setConfig(c => ({ ...c, [key]: [...((c as any)[key] ?? []), item] }));
  const listRemove = (key: keyof HomeConfig, i: number) =>
    setConfig(c => { const a = [...((c as any)[key] ?? [])]; a.splice(i, 1); return { ...c, [key]: a }; });
  const listUpdate = (key: keyof HomeConfig, i: number, k: string, v: any) =>
    setConfig(c => { const a = [...((c as any)[key] ?? [])]; a[i] = { ...a[i], [k]: v }; return { ...c, [key]: a }; });

  const footerListAdd    = (key: "socialLinks" | "trustBadges" | "bottomLinks", item: object) =>
    setConfig(c => ({ ...c, footer: { ...c.footer, [key]: [...(c.footer[key] ?? []), item] } }));
  const footerListRemove = (key: "socialLinks" | "trustBadges" | "bottomLinks", i: number) =>
    setConfig(c => { const a = [...(c.footer[key] ?? [])]; a.splice(i, 1); return { ...c, footer: { ...c.footer, [key]: a } }; });
  const footerListUpdate = (key: "socialLinks" | "trustBadges" | "bottomLinks", i: number, k: string, v: string) =>
    setConfig(c => { const a = [...(c.footer[key] as any[])]; a[i] = { ...a[i], [k]: v }; return { ...c, footer: { ...c.footer, [key]: a } }; });

  /* ─────────────────────────────────────────────
     TAB CONTENTS
  ───────────────────────────────────────────── */

  /* ── MARKA & TEMA ── */
  const TabMarka = () => (
    <div className="space-y-6">

      {/* Site kimliği */}
      <Card title="Site Kimliği" icon={<Globe size={16} />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Site Adı" hint="Header'da görünen marka adı">
            <Input value={config.brand?.siteName ?? ""} onChange={e => setBrand("siteName", e.target.value)} placeholder="PORTAKAL3D" />
          </Field>
          <Field label="Site Alt Başlığı" hint="Header'da site adının altında görünür">
            <Input value={config.brand?.siteTagline ?? ""} onChange={e => setBrand("siteTagline", e.target.value)} placeholder="3D Yazıcı & Filament" />
          </Field>

          {/* Logo */}
          <Field label="Logo" col2>
            <div className="flex items-start gap-4">
              <div className="h-16 w-16 rounded-xl border border-gray-200 bg-gray-50 grid place-items-center overflow-hidden shrink-0">
                {logoPreview || config.brand?.logoUrl ? (
                  <img src={logoPreview ?? config.brand?.logoUrl} alt="logo" className="h-full w-full object-contain" />
                ) : (
                  <Image size={22} className="text-gray-300" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50 transition">
                  <Upload size={14} /> Dosya Seç
                  <input type="file" accept="image/*" className="hidden" onChange={e => {
                    const f = e.target.files?.[0] ?? null;
                    setLogoFile(f);
                    if (f) { const r = new FileReader(); r.onload = ev => setLogoPreview(ev.target?.result as string); r.readAsDataURL(f); }
                    else setLogoPreview(null);
                  }} />
                </label>
                {logoFile && <p className="text-xs text-green-600">"{logoFile.name}" kaydedildiğinde yüklenecek</p>}
                <Input
                  placeholder="veya URL girin: https://..."
                  value={config.brand?.logoUrl ?? ""}
                  onChange={e => setBrand("logoUrl", e.target.value)}
                />
              </div>
            </div>
          </Field>
        </div>
      </Card>

      {/* Tema renkleri */}
      <Card title="Tema Renkleri" icon={<Palette size={16} />} action={
        <button
          type="button"
          onClick={() => setConfig(c => ({ ...c, theme: {
            gradientHero: "linear-gradient(135deg, #020617 0%, #111827 40%, #FACC15 100%)",
            backgroundColor: "#020617", accent: "#FACC15", accentHover: "#EAB308",
            textPrimary: "#F9FAFB", textSecondary: "#E5E7EB",
          }}))}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-yellow-400 bg-black text-yellow-300 hover:bg-zinc-900"
        >
          <span className="inline-flex h-3 w-3 rounded-full overflow-hidden border border-zinc-600">
            <span className="w-1/2 bg-black" /><span className="w-1/2 bg-yellow-400" />
          </span>
          Siyah & Sarı Preset
        </button>
      }>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Hero Gradient (CSS)" col2 hint='Örn: linear-gradient(135deg, #000 0%, #F7C948 100%)'>
            <Input value={config.theme.gradientHero} onChange={e => setTheme("gradientHero", e.target.value)} />
          </Field>
          {[
            { key: "backgroundColor" as const, label: "Arka Plan" },
            { key: "accent" as const,          label: "Ana Renk (Accent)" },
            { key: "accentHover" as const,     label: "Accent Hover" },
            { key: "textPrimary" as const,     label: "Ana Metin" },
            { key: "textSecondary" as const,   label: "Alt Metin" },
          ].map(({ key, label }) => (
            <Field key={key} label={label}>
              <div className="flex items-center gap-2">
                <input type="color" className="h-9 w-12 p-0.5 rounded border border-gray-200 cursor-pointer"
                  value={config.theme[key] || "#ffffff"} onChange={e => setTheme(key, e.target.value)} />
                <Input value={config.theme[key] || ""} onChange={e => setTheme(key, e.target.value)} placeholder="#000000" />
              </div>
            </Field>
          ))}
        </div>
      </Card>
    </div>
  );

  /* ── İÇERİK ── */
  const TabIcerik = () => (
    <div className="space-y-6">

      {/* Hero metinleri */}
      <Card title="Hero Metinleri" icon={<Layout size={16} />}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Başlık">
            <Input value={config.hero.title} onChange={e => setHero("title", e.target.value)} />
          </Field>
          <Field label="Alt Başlık">
            <Input value={config.hero.subtitle} onChange={e => setHero("subtitle", e.target.value)} />
          </Field>
          <Field label="Birincil Buton">
            <Input value={config.hero.primaryCta} onChange={e => setHero("primaryCta", e.target.value)} />
          </Field>
          <Field label="İkincil Buton">
            <Input value={config.hero.secondaryCta} onChange={e => setHero("secondaryCta", e.target.value)} />
          </Field>
          <Field label="Slider Sloganı" hint="Slider'ın içinde görünen kısa slogan">
            <Input value={config.brand?.tagline ?? ""} onChange={e => setBrand("tagline", e.target.value)} placeholder="Konfor • Kalite • Hız" />
          </Field>
        </div>
      </Card>

      {/* Hero Slider */}
      <Card title="Hero Slider Görselleri" icon={<Image size={16} />} action={
        <label className="inline-flex items-center gap-1.5 text-sm text-blue-600 cursor-pointer hover:text-blue-700 font-medium">
          <Upload size={14} /> Görsel Ekle
          <input type="file" accept="image/*" multiple className="hidden" onChange={e => {
            if (e.target.files) setNewFiles(prev => [...prev, ...Array.from(e.target.files!)]);
          }} />
        </label>
      }>
        {newFiles.length > 0 && (
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            {newFiles.length} yeni görsel kaydedildiğinde yüklenecek
            <button onClick={() => setNewFiles([])} className="ml-2 text-blue-500 hover:text-blue-700 underline">iptal</button>
          </div>
        )}
        {(config.heroSlider ?? []).length === 0 && newFiles.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">Henüz slider görseli eklenmemiş.</p>
        )}
        <div className="space-y-3">
          {(config.heroSlider ?? []).map((sl, i) => (
            <div key={i} className="grid grid-cols-[80px_1fr_auto] gap-3 p-3 border border-gray-100 rounded-xl">
              <div className="rounded-lg bg-gray-100 overflow-hidden aspect-video">
                {sl.imageUrl
                  ? <img src={sl.imageUrl} alt="" className="w-full h-full object-cover" />
                  : <div className="h-full grid place-items-center text-gray-400 text-xs">Görsel yok</div>
                }
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Başlık" value={sl.headline ?? ""} onChange={e => listUpdate("heroSlider", i, "headline", e.target.value)} className="col-span-2" />
                <Input placeholder="Açıklama" value={sl.caption ?? ""} onChange={e => listUpdate("heroSlider", i, "caption", e.target.value)} className="col-span-2" />
                <Input placeholder="Buton Yazısı" value={sl.ctaLabel ?? ""} onChange={e => listUpdate("heroSlider", i, "ctaLabel", e.target.value)} />
                <Input placeholder="Buton Linki" value={sl.ctaHref ?? ""} onChange={e => listUpdate("heroSlider", i, "ctaHref", e.target.value)} />
              </div>
              <DeleteBtn onClick={() => {
                if (sl.imageUrl) setRemovedUrls(p => [...p, sl.imageUrl]);
                listRemove("heroSlider", i);
              }} />
            </div>
          ))}
        </div>
      </Card>

      {/* Kayan yazılar */}
      <Card title="Kayan Yazılar (Duyuru Barı)" icon={<MessageSquare size={16} />} action={
        <button onClick={() => listAdd("announcements", { text: "", to: "" })} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
          <Plus size={14} /> Ekle
        </button>
      }>
        {config.announcements.length === 0 && <p className="text-sm text-gray-400">Kayan yazı yok.</p>}
        <div className="space-y-2">
          {config.announcements.map((a, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="Mesaj" value={a.text} onChange={e => listUpdate("announcements", i, "text", e.target.value)} />
              <Input placeholder="Link (opsiyonel)" value={a.to ?? ""} onChange={e => listUpdate("announcements", i, "to", e.target.value)} className="max-w-[200px]" />
              <DeleteBtn onClick={() => listRemove("announcements", i)} />
            </div>
          ))}
        </div>
      </Card>

      {/* Bölüm başlıkları */}
      <Card title="Bölüm Başlıkları">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(Object.entries({
            featured: "Öne Çıkan Ürünler",
            stats:    "İstatistikler",
            process:  "Çalışma Süreci",
            services: "Hizmetler",
            faqs:     "SSS",
            contact:  "İletişim",
          }) as [keyof HomeConfig["sections"], string][]).map(([key, name]) => (
            <div key={key} className="p-3 border border-gray-100 rounded-xl space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{name}</p>
              <Input placeholder="Başlık" value={(config.sections[key] as any).title ?? ""} onChange={e => setSection(key, "title", e.target.value)} />
              <Input placeholder="Alt başlık" value={(config.sections[key] as any).subtitle ?? ""} onChange={e => setSection(key, "subtitle", e.target.value)} />
              {key === "featured" && (
                <Input placeholder="Buton yazısı" value={config.sections.featured.buttonText ?? ""} onChange={e => setSection("featured", "buttonText", e.target.value)} />
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  /* ── İLETİŞİM & FOOTER ── */
  const TabIletisim = () => (
    <div className="space-y-6">

      <Card title="Firma Bilgileri" icon={<Globe size={16} />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Firma Adı">
            <Input value={config.footer.companyName} onChange={e => setFooter("companyName", e.target.value)} />
          </Field>
          <Field label="Firma Sloganı">
            <Input value={config.footer.companyTagline} onChange={e => setFooter("companyTagline", e.target.value)} />
          </Field>
          <Field label="Web Sitesi">
            <Input value={config.footer.website} onChange={e => setFooter("website", e.target.value)} placeholder="www.orneksite.com" />
          </Field>
          <Field label="Yetkili Kişi">
            <Input value={config.footer.contactPerson} onChange={e => setFooter("contactPerson", e.target.value)} />
          </Field>
          <Field label="Adres" col2>
            <Input value={config.footer.addressLine} onChange={e => setFooter("addressLine", e.target.value)} />
          </Field>
          <Field label="Footer Açıklaması" col2>
            <Textarea rows={3} value={config.footer.description} onChange={e => setFooter("description", e.target.value)} />
          </Field>
        </div>
      </Card>

      <Card title="İletişim Bilgileri">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Telefon Gösterimi" hint='Ör: +90 543 265 45 24'>
            <Input value={config.footer.phoneDisplay} onChange={e => setFooter("phoneDisplay", e.target.value)} />
          </Field>
          <Field label="Telefon Linki" hint='Ör: tel:+905432654524'>
            <Input value={config.footer.phoneHref} onChange={e => setFooter("phoneHref", e.target.value)} />
          </Field>
          <Field label="Mail Gösterimi">
            <Input value={config.footer.mailDisplay} onChange={e => setFooter("mailDisplay", e.target.value)} />
          </Field>
          <Field label="Mail Linki" hint='Ör: mailto:info@site.com'>
            <Input value={config.footer.mailHref} onChange={e => setFooter("mailHref", e.target.value)} />
          </Field>
          <Field label="WhatsApp Numarası" hint='Ör: 905432654524 (başında + yok)'>
            <Input value={config.footer.whatsappNumber} onChange={e => setFooter("whatsappNumber", e.target.value)} />
          </Field>
          <Field label="WhatsApp Buton Yazısı">
            <Input value={config.footer.whatsappButtonText} onChange={e => setFooter("whatsappButtonText", e.target.value)} />
          </Field>
        </div>
      </Card>

      <Card title="Harita" icon={<Globe size={16} />}>
        <Field label="Google Maps Embed URL" hint="Google Maps → Paylaş → Haritayı Göm → iframe src değeri">
          <Input value={config.mapEmbedUrl} onChange={e => setConfig(c => ({ ...c, mapEmbedUrl: e.target.value }))} />
        </Field>
      </Card>

      <Card title="Sosyal Medya" action={
        <button onClick={() => footerListAdd("socialLinks", { platform: "", label: "", href: "" })} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
          <Plus size={14} /> Ekle
        </button>
      }>
        {config.footer.socialLinks.length === 0 && <p className="text-sm text-gray-400">Sosyal medya linki yok.</p>}
        <div className="space-y-2">
          {config.footer.socialLinks.map((s, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="Platform (Instagram)" value={s.platform} onChange={e => footerListUpdate("socialLinks", i, "platform", e.target.value)} className="max-w-[140px]" />
              <Input placeholder="Etiket" value={s.label} onChange={e => footerListUpdate("socialLinks", i, "label", e.target.value)} />
              <Input placeholder="URL" value={s.href} onChange={e => footerListUpdate("socialLinks", i, "href", e.target.value)} />
              <DeleteBtn onClick={() => footerListRemove("socialLinks", i)} />
            </div>
          ))}
        </div>
      </Card>

      <Card title="Güven Rozetleri" action={
        <button onClick={() => footerListAdd("trustBadges", { icon: "", title: "", desc: "" })} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
          <Plus size={14} /> Ekle
        </button>
      }>
        <p className="text-xs text-gray-400 mb-3">İkon adı için: Truck, ShieldCheck, CreditCard, Phone vb.</p>
        <div className="space-y-2">
          {config.footer.trustBadges.map((b, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="İkon" value={b.icon} onChange={e => footerListUpdate("trustBadges", i, "icon", e.target.value)} className="max-w-[120px]" />
              <Input placeholder="Başlık" value={b.title} onChange={e => footerListUpdate("trustBadges", i, "title", e.target.value)} />
              <Input placeholder="Açıklama" value={b.desc} onChange={e => footerListUpdate("trustBadges", i, "desc", e.target.value)} />
              <DeleteBtn onClick={() => footerListRemove("trustBadges", i)} />
            </div>
          ))}
        </div>
      </Card>

      <Card title="Alt Linkler (Footer)" action={
        <button onClick={() => footerListAdd("bottomLinks", { label: "", to: "" })} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
          <Plus size={14} /> Ekle
        </button>
      }>
        <div className="space-y-2">
          {config.footer.bottomLinks.map((l, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="Etiket" value={l.label} onChange={e => footerListUpdate("bottomLinks", i, "label", e.target.value)} />
              <Input placeholder="Yol (ör: /gizlilik)" value={l.to} onChange={e => footerListUpdate("bottomLinks", i, "to", e.target.value)} />
              <DeleteBtn onClick={() => footerListRemove("bottomLinks", i)} />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  /* ── SAYFALAR (Hizmetler, Süreç, Stats, Pricing, SSS, İletişim) ── */
  const TabSayfalar = () => (
    <div className="space-y-6">

      {/* Hizmetler */}
      <Card title="Hizmetler" action={
        <AddBtn onClick={() => listAdd("services", { icon: "", title: "", description: "" })} label="Ekle" />
      }>
        <p className="text-xs text-gray-400 mb-3">İkon: Scissors, Palette, Thermometer, Wrench vb.</p>
        <div className="space-y-2">
          {config.services.map((s, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="İkon" value={s.icon} onChange={e => listUpdate("services", i, "icon", e.target.value)} className="max-w-[100px]" />
              <Input placeholder="Başlık" value={s.title} onChange={e => listUpdate("services", i, "title", e.target.value)} />
              <Input placeholder="Açıklama" value={s.description} onChange={e => listUpdate("services", i, "description", e.target.value)} />
              <DeleteBtn onClick={() => listRemove("services", i)} />
            </div>
          ))}
          {config.services.length === 0 && <p className="text-sm text-gray-400">Hizmet eklenmemiş.</p>}
        </div>
      </Card>

      {/* Süreç */}
      <Card title="Çalışma Süreci" action={
        <AddBtn onClick={() => listAdd("processSteps", { icon: "", title: "", desc: "" })} label="Ekle" />
      }>
        <div className="space-y-2">
          {config.processSteps.map((s, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="İkon" value={s.icon} onChange={e => listUpdate("processSteps", i, "icon", e.target.value)} className="max-w-[100px]" />
              <Input placeholder="Başlık" value={s.title} onChange={e => listUpdate("processSteps", i, "title", e.target.value)} />
              <Input placeholder="Açıklama" value={s.desc} onChange={e => listUpdate("processSteps", i, "desc", e.target.value)} />
              <DeleteBtn onClick={() => listRemove("processSteps", i)} />
            </div>
          ))}
          {config.processSteps.length === 0 && <p className="text-sm text-gray-400">Adım eklenmemiş.</p>}
        </div>
      </Card>

      {/* İstatistikler */}
      <Card title="İstatistikler (Hızlı Bakış)" action={
        <AddBtn onClick={() => listAdd("stats", { icon: "", number: "", label: "" })} label="Ekle" />
      }>
        <div className="space-y-2">
          {config.stats.map((s, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="İkon" value={s.icon} onChange={e => listUpdate("stats", i, "icon", e.target.value)} className="max-w-[100px]" />
              <Input placeholder="Sayı (ör: 1200+)" value={s.number} onChange={e => listUpdate("stats", i, "number", e.target.value)} className="max-w-[120px]" />
              <Input placeholder="Etiket" value={s.label} onChange={e => listUpdate("stats", i, "label", e.target.value)} />
              <DeleteBtn onClick={() => listRemove("stats", i)} />
            </div>
          ))}
          {config.stats.length === 0 && <p className="text-sm text-gray-400">İstatistik eklenmemiş.</p>}
        </div>
      </Card>

      {/* Paketler */}
      <Card title="Paketler / Fiyatlandırma" action={
        <AddBtn onClick={() => listAdd("pricing", { tier: "", price: "", features: [] })} label="Paket Ekle" />
      }>
        <div className="space-y-4">
          {config.pricing.map((p, i) => (
            <div key={i} className="border border-gray-100 rounded-xl p-4 space-y-3">
              <div className="flex gap-2">
                <Input placeholder="Paket adı" value={p.tier} onChange={e => listUpdate("pricing", i, "tier", e.target.value)} />
                <Input placeholder="Fiyat (₺1.950)" value={p.price} onChange={e => listUpdate("pricing", i, "price", e.target.value)} className="max-w-[140px]" />
                <DeleteBtn onClick={() => listRemove("pricing", i)} />
              </div>
              <div className="space-y-1.5 pl-2 border-l-2 border-gray-100">
                {p.features.map((f, j) => (
                  <div key={j} className="flex gap-2">
                    <Input placeholder="Özellik" value={f} onChange={e => {
                      const arr = [...p.features]; arr[j] = e.target.value;
                      setConfig(c => { const pr = [...c.pricing]; pr[i] = { ...pr[i], features: arr }; return { ...c, pricing: pr }; });
                    }} />
                    <DeleteBtn onClick={() => {
                      const arr = [...p.features]; arr.splice(j, 1);
                      setConfig(c => { const pr = [...c.pricing]; pr[i] = { ...pr[i], features: arr }; return { ...c, pricing: pr }; });
                    }} />
                  </div>
                ))}
                <button className="text-xs text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1 mt-1" onClick={() => {
                  setConfig(c => { const pr = [...c.pricing]; pr[i] = { ...pr[i], features: [...pr[i].features, ""] }; return { ...c, pricing: pr }; });
                }}>
                  <Plus size={12} /> Özellik ekle
                </button>
              </div>
            </div>
          ))}
          {config.pricing.length === 0 && <p className="text-sm text-gray-400">Paket eklenmemiş.</p>}
        </div>
      </Card>

      {/* SSS */}
      <Card title="Sıkça Sorulan Sorular" action={
        <AddBtn onClick={() => listAdd("faqs", { q: "", a: "" })} label="Ekle" />
      }>
        <div className="space-y-3">
          {config.faqs.map((f, i) => (
            <div key={i} className="flex gap-2 items-start">
              <div className="flex-1 space-y-1.5">
                <Input placeholder="Soru" value={f.q} onChange={e => listUpdate("faqs", i, "q", e.target.value)} />
                <Textarea rows={2} placeholder="Cevap" value={f.a} onChange={e => listUpdate("faqs", i, "a", e.target.value)} />
              </div>
              <DeleteBtn onClick={() => listRemove("faqs", i)} />
            </div>
          ))}
          {config.faqs.length === 0 && <p className="text-sm text-gray-400">SSS eklenmemiş.</p>}
        </div>
      </Card>

      {/* İletişim sayfası bilgileri */}
      <Card title="İletişim Sayfası Bilgileri">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Telefon">
            <Input value={config.contact.phone} onChange={e => setContact("phone", e.target.value)} />
          </Field>
          <Field label="E-posta">
            <Input type="email" value={config.contact.email} onChange={e => setContact("email", e.target.value)} />
          </Field>
          <Field label="Adres">
            <Input value={config.contact.address} onChange={e => setContact("address", e.target.value)} />
          </Field>
        </div>
      </Card>
    </div>
  );

  /* ── AYARLAR ── */
  const TabAyarlar = () => (
    <div className="space-y-6">

      {/* SMS Bildirimleri */}
      <Card title="SMS Bildirimleri" icon={<MessageSquare size={16} />}>
        <div className="divide-y divide-gray-100">
          <div className="flex items-center justify-between py-4 first:pt-0">
            <div>
              <p className="text-sm font-medium text-gray-800">Sipariş SMS Bildirimi</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Yeni sipariş geldiğinde aşağıdaki numaraya otomatik SMS gönderilir.
              </p>
            </div>
            <Toggle
              checked={config.notifications?.smsAktif ?? true}
              onChange={() => setConfig(c => ({
                ...c,
                notifications: { ...(c.notifications ?? {}), smsAktif: !(c.notifications?.smsAktif ?? true) }
              }))}
            />
          </div>
          <div className="py-4 last:pb-0">
            <Field label="Bildirim Telefon Numarası" hint="Ör: 05321234567 veya +905321234567 — sipariş SMS'i bu numaraya gider">
              <Input
                placeholder="05321234567"
                value={config.notifications?.smsPhone ?? ""}
                onChange={e => setConfig(c => ({
                  ...c,
                  notifications: { ...(c.notifications ?? {}), smsPhone: e.target.value }
                }))}
              />
            </Field>
          </div>
        </div>
      </Card>

      {/* Sipariş & Ödeme */}
      <Card title="Sipariş & Ödeme Ayarları" icon={<Settings size={16} />}>
        <div className="divide-y divide-gray-100">
          <div className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
            <div>
              <p className="text-sm font-medium text-gray-800">Ödemesiz Sipariş</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Açıksa checkout sayfasında "Ödeme Yap" ile birlikte "Ödemesiz Sipariş Ver" butonu da görünür.
              </p>
            </div>
            <Toggle
              checked={config.settings?.odemesizSiparisAktifMi ?? false}
              onChange={() => setConfig(c => ({ ...c, settings: { ...(c.settings ?? {}), odemesizSiparisAktifMi: !(c.settings?.odemesizSiparisAktifMi ?? false) } }))}
            />
          </div>
        </div>
      </Card>
    </div>
  );

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <h1 className="text-base font-semibold text-gray-800">Site Konfigürasyonu</h1>
            <button
              onClick={handleSave}
              disabled={loading}
              className={[
                "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition",
                saved
                  ? "bg-green-500 text-white"
                  : "bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60",
              ].join(" ")}
            >
              {loading ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              {loading ? "Kaydediliyor…" : saved ? "Kaydedildi ✓" : recordId ? "Güncelle" : "Kaydet"}
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto pb-0 -mb-px">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={[
                  "inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors",
                  activeTab === tab.id
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                ].join(" ")}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {loading && !config && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-gray-400" size={28} />
          </div>
        )}
        {activeTab === "marka"    && <TabMarka />}
        {activeTab === "icerik"   && <TabIcerik />}
        {activeTab === "iletisim" && <TabIletisim />}
        {activeTab === "sayfalar" && <TabSayfalar />}
        {activeTab === "ayarlar"  && <TabAyarlar />}
      </div>
    </div>
  );
};

export default AdminHomeConfigPage;
